import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEO } from '../components/SEO';
import { ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Initiative data
const initiatives = [
  {
    id: 'work-experience-2026',
    title: 'Work Experience Program',
    subtitle: 'Real-World Experience for Year 12 Students',
    year: '2026',
    summary: 'A two-week placement giving Year 12 students from the AASMA network their first real experience of professional work, with genuine tasks, real responsibility, and a finished piece of work to call their own.',
    link: '/giving-back/work-experience-2026'
  },
  {
    id: 'aasma-2025',
    title: 'AASMA',
    subtitle: 'Australia-Asia Syndicate for Mentorship & Advancement',
    year: '2025',
    summary: 'A cross-border mentorship program connecting accomplished Australian women leaders with aspiring female entrepreneurs and professionals in Pakistan.',
    link: '/giving-back/aasma-2025'
  },
  {
    id: 'career-consults-2024',
    title: 'Career Consults',
    subtitle: 'Free One-on-One Career Consultations',
    year: '2024',
    summary: 'Free 30-minute consultations offered to anyone seeking guidance on organisational agility. Every available slot was booked out within minutes of the announcement.',
    link: '/giving-back/career-consults-2024'
  },
  {
    id: 'workshops-2023',
    title: 'Training Workshops',
    subtitle: 'Ways of Working Workshops',
    year: '2023',
    summary: 'A series of free workshops for delivery leads, iteration managers, project managers, and agile coaches covering flow fundamentals, batch sizing, estimation, and iterative development.',
    link: '/giving-back/workshops-2023'
  }
];

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-white" data-testid="givingback-hero">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] mb-6">
            #GivingBack
          </h1>
          <p className="text-lg md:text-xl text-[#4a4744] leading-relaxed italic">
            Paying forward the belief, support, and opportunity that was extended to us.
          </p>
        </div>
      </div>
    </section>
  );
};

// Narrative Section
const NarrativeSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-12 md:py-16 bg-[#faf9f7]" data-testid="givingback-narrative">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="space-y-6 text-[#4a4744] leading-relaxed">
            <p>
              In 2010, Agilitie founder Nadir Khan arrived in Australia with no job, no savings, no network, and a baby on the way. On paper, the odds were stacked against him. But this country, its people, and its professional community embraced him with open arms.
            </p>
            <p>
              Over the years that followed, he was lifted, guided, mentored, and believed in by those around him.
            </p>
            <p>
              #GivingBack is how Agilitie honours that experience. Each year in May, the firm commits to a pro bono initiative designed to return something meaningful to the people, communities, and countries that helped shape who we are today. Every initiative is different, but the intent is always the same: to pay forward the belief, support, and opportunity that was extended to us.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Initiative Card
const InitiativeCard = ({ initiative, index }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link 
      to={initiative.link}
      ref={ref}
      className={`block transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 100}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`initiative-card-${initiative.id}`}
    >
      <div className="relative bg-white border border-[#e5e2dc] p-8 md:p-10 overflow-hidden">
        {/* Orange top bar animation */}
        <div 
          className="absolute top-0 left-0 h-[3px] bg-[#d4782c] transition-all duration-300"
          style={{ width: isHovered ? '100%' : '0%' }}
        />

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex-1">
            <span className="inline-block px-3 py-1 bg-[#faf9f7] text-[#d4782c] font-dm-sans font-semibold text-sm mb-4">
              {initiative.year}
            </span>
            <h3 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-2">
              {initiative.title}
            </h3>
            <p className="text-[#d4782c] font-dm-sans font-medium mb-4">
              {initiative.subtitle}
            </p>
            <p className="text-[#4a4744] leading-relaxed">
              {initiative.summary}
            </p>
          </div>

          <div className="flex items-center gap-2 text-[#d4782c] font-dm-sans font-semibold shrink-0">
            <span>Learn more</span>
            <ArrowRight 
              size={18} 
              className="transition-transform duration-200"
              style={{ transform: isHovered ? 'translateX(4px)' : 'translateX(0)' }}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Initiatives Section
const InitiativesSection = () => {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="givingback-initiatives">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-8">
          Our Initiatives
        </h2>
        <div className="space-y-6">
          {initiatives.map((initiative, index) => (
            <InitiativeCard key={initiative.id} initiative={initiative} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Interest Form Section
const InterestFormSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interests: [],
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const interestOptions = [
    { id: 'updates', label: 'Keep me updated on future #GivingBack initiatives' },
    { id: 'volunteer', label: "I'd like to contribute my time or skills to a future program" }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleInterestChange = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.interests.length === 0) {
      setStatus({ type: 'error', message: 'Please select at least one option for how you would like to get involved.' });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Get reCAPTCHA token
      let recaptchaToken = null;
      if (window.grecaptcha && RECAPTCHA_SITE_KEY) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'givingback_interest' });
        } catch (recaptchaError) {
          logger.warn('reCAPTCHA failed:', recaptchaError);
        }
      }

      const interestLabels = formData.interests.map(id => 
        interestOptions.find(opt => opt.id === id)?.label
      ).join(', ');

      await axios.post(`${API}/givingback/interest`, {
        name: formData.name,
        email: formData.email,
        interests: interestLabels,
        message: formData.message || '',
        recaptcha_token: recaptchaToken
      });

      setStatus({ type: 'success', message: 'Thank you for your interest! We will be in touch.' });
      setFormData({ name: '', email: '', interests: [], message: '' });
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Something went wrong. Please try again.' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="get-involved" className="py-16 md:py-20 bg-[#faf9f7]" data-testid="givingback-form">
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4 text-center">
            Get Involved
          </h2>
          <p className="text-[#4a4744] text-center mb-10">
            Whether you want to stay informed about upcoming initiatives or contribute your time and skills, we'd love to hear from you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {status.type === 'success' ? (
              <div className="text-center py-12 px-6 bg-white border border-[#e5e2dc]">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a] mb-2">
                  Thank you!
                </h3>
                <p className="text-[#4a4744]">
                  Your submission has been received. We will be in touch soon.
                </p>
              </div>
            ) : (
              <>
                {status.type === 'error' && (
                  <div className="p-4 bg-red-50 text-red-800 border border-red-200">
                    {status.message}
                  </div>
                )}

            <div>
              <label htmlFor="name" className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                Name <span className="text-[#d4782c]">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className="w-full px-4 py-3 border border-[#e5e2dc] bg-white font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors"
                data-testid="givingback-name-input"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                Email <span className="text-[#d4782c]">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className="w-full px-4 py-3 border border-[#e5e2dc] bg-white font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors"
                data-testid="givingback-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-3">
                I'm interested in <span className="text-[#d4782c]">*</span>
              </label>
              <div className="space-y-3">
                {interestOptions.map((option) => (
                  <label key={option.id} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.interests.includes(option.id)}
                      onChange={() => handleInterestChange(option.id)}
                      className="mt-1 w-5 h-5 border-2 border-[#e5e2dc] rounded-none accent-[#d4782c] cursor-pointer"
                      data-testid={`givingback-interest-${option.id}`}
                    />
                    <span className="text-[#4a4744] group-hover:text-[#1a1a1a] transition-colors">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                Optional message
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-[#e5e2dc] bg-white font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors resize-none"
                data-testid="givingback-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1a1a1a] text-white py-4 font-dm-sans font-semibold hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="givingback-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>

            <p className="text-xs text-[#4a4744] text-center">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a> apply.
            </p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default function GivingBackPage() {
  return (
    <Layout>
      <SEO 
        title="#GivingBack"
        path="/giving-back"
        description="Each year in May, Agilitie commits to a pro bono initiative designed to return something meaningful to the people, communities, and countries that helped shape who we are today."
      />
      <HeroSection />
      <NarrativeSection />
      <InitiativesSection />
      <InterestFormSection />
    </Layout>
  );
}
